"use client";

import { useConfirm } from "@/components/ConfirmDialog";
import { BuildingIcon, PencilIcon, TrashIcon } from "@/components/icons";
import { RequestError, requestJson } from "@/lib/clientRequest";
import type { SidebarBusiness } from "./types";

/** The business select with its create, rename and delete actions. */
export function BusinessSwitcher({
  businesses,
  activeBusiness,
  onSwitch,
  refreshAll,
  refreshBusinesses,
}: {
  businesses: SidebarBusiness[];
  activeBusiness: string;
  onSwitch: (id: string) => void;
  /** Reloads the sidebar's lists and the server components after a change. */
  refreshAll: () => Promise<void>;
  refreshBusinesses: () => Promise<void>;
}) {
  const ask = useConfirm();

  async function addBusiness() {
    const name = prompt("New business name");
    if (!name?.trim()) return;
    try {
      const data = await requestJson("/api/businesses", {
        method: "POST",
        body: { name },
        fallback: "Could not create business",
      });
      await refreshBusinesses();
      onSwitch(data.business.id);
    } catch (cause) {
      if (!(cause instanceof RequestError)) throw cause;
      alert(cause.message);
    }
  }

  async function renameBusiness() {
    const current = businesses.find((b) => b.id === activeBusiness);
    if (!current) return;
    const name = prompt("Rename business", current.name);
    if (!name?.trim() || name.trim() === current.name) return;
    try {
      await requestJson(`/api/businesses/${current.id}`, {
        method: "PATCH",
        body: { name },
        fallback: "Could not rename business",
      });
      await refreshAll();
    } catch (cause) {
      if (!(cause instanceof RequestError)) throw cause;
      alert(cause.message);
    }
  }

  async function removeBusiness() {
    const current = businesses.find((b) => b.id === activeBusiness);
    if (!current) return;
    if (!(await ask({ title: `Delete business "${current.name}"?`, message: "Its departments are removed. Workers must be deleted first.", confirmLabel: "Delete business" })))
      return;
    try {
      await requestJson(`/api/businesses/${current.id}`, {
        method: "DELETE",
        fallback: "Could not delete business",
      });
      await refreshAll();
    } catch (cause) {
      if (!(cause instanceof RequestError)) throw cause;
      alert(cause.message);
    }
  }

  return (
    <div data-business-switcher-row className="flex items-center gap-1.5 md:h-11">
      <BuildingIcon className="size-4 shrink-0 text-zinc-400" />
      <label className="sr-only" htmlFor="business-switcher">
        Business
      </label>
      <select
        id="business-switcher"
        value={
          businesses.some((b) => b.id === activeBusiness)
            ? activeBusiness
            : ""
        }
        onChange={(e) => onSwitch(e.target.value)}
        className="min-w-0 flex-1 truncate rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm font-medium text-zinc-100 outline-none focus:border-violet-500/60"
      >
        {businesses.length === 0 ||
        !businesses.some((b) => b.id === activeBusiness) ? (
          <option value="" disabled>
            {businesses.length === 0 ? "Loading…" : "Select business"}
          </option>
        ) : null}
        {businesses.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        title="New business"
        onClick={() => void addBusiness()}
        className="rounded-md p-1.5 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200"
      >
        <span className="block text-sm leading-none">+</span>
      </button>
      <button
        type="button"
        title="Rename business"
        onClick={() => void renameBusiness()}
        disabled={!activeBusiness}
        className="rounded-md p-1.5 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-40"
      >
        <PencilIcon className="size-3.5" />
      </button>
      <button
        type="button"
        title="Delete business"
        onClick={() => void removeBusiness()}
        disabled={!activeBusiness || businesses.length <= 1}
        className="rounded-md p-1.5 text-zinc-500 transition hover:bg-zinc-800 hover:text-zinc-200 disabled:opacity-40"
      >
        <TrashIcon className="size-3.5" />
      </button>
    </div>
  );
}
